import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api.js';
import { useAuth } from './AuthContext.jsx';
import { FAMILY_CODE_KEY, MEMBER_SECRET_KEY } from '../constants.js';

const DeleteAccountContext = createContext(null);

// Storage flags written by RecoveryEmailPromptContext (kept in sync manually).
const RECOVERY_AUTO_PROMPT_KEY = 'ptcmRecoveryEmailAutoPromptSeen';
const RECOVERY_ENGAGED_KEY = 'ptcmRecoveryReminderEngaged';

export function DeleteAccountProvider({ children }) {
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const open = useCallback(() => {
    setError('');
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    if (isSubmitting) return;
    setIsOpen(false);
    setError('');
  }, [isSubmitting]);

  const confirmDelete = useCallback(async () => {
    setIsSubmitting(true);
    setError('');

    try {
      await apiFetch('/api/family/delete', { token, method: 'POST' });

      // Wipe any locally stored credentials so the device returns to a clean logged-out state.
      try {
        localStorage.removeItem(MEMBER_SECRET_KEY);
        localStorage.removeItem(FAMILY_CODE_KEY);
        localStorage.removeItem(RECOVERY_AUTO_PROMPT_KEY);
        localStorage.removeItem(RECOVERY_ENGAGED_KEY);
      } catch (e) {
        // ignore storage errors
      }

      logout(); // removes the auth token and resets auth state
      setIsSubmitting(false);
      setIsOpen(false);
      navigate('/', { replace: true });
      return true;
    } catch (err) {
      setError(err?.message || 'La suppression a échoué. Réessayez.');
      setIsSubmitting(false);
      throw err;
    }
  }, [token, logout, navigate]);

  const value = useMemo(
    () => ({ isOpen, isSubmitting, error, open, close, confirmDelete }),
    [isOpen, isSubmitting, error, open, close, confirmDelete]
  );

  return <DeleteAccountContext.Provider value={value}>{children}</DeleteAccountContext.Provider>;
}

export function useDeleteAccount() {
  const ctx = useContext(DeleteAccountContext);
  if (!ctx) throw new Error('useDeleteAccount must be used within DeleteAccountProvider');
  return ctx;
}
